import * as React from 'react'
import {
  EditModalActionBar,
  ConfirmDeleteModal,
  FormikTextField,
} from 'components'
import {
  Box,
  Form,
  Grid,
  Typography,
  IconButton,
  Checkbox,
  Button,
} from 'cs-kevlar-react'
import { Formik } from 'formik'
import { Spacing } from 'cs-kevlar-react/lib/es/theming/baseConstants'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { MapLayer } from '../../api'
import * as Yup from 'yup'
import { useAuth } from 'hooks'
import { useCurrentAgencyCustomMapLayers } from 'hooks/useCurrentAgencyCustomMapLayers'
import { LinearProgress } from '@mui/material'
import { useAgency } from 'hooks/useAgency'
import { Clear } from 'cs-kevlar-react/lib/es/icons'

export type SaveProgress = 
  | number
  | 'indeterminate'
interface EditCustomMapLayerProps {
  reinitializeForm?: boolean
  customMapLayer: MapLayer
  onClose: () => void
  onSave: (values: MapLayer, file: File | null) => Promise<void>
  onAdd?: (values: MapLayer, file: File | null) => Promise<void>
  onDelete?: () => Promise<void>
  canDelete: boolean
  saveText: string
  confirmColor?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
  didActionComplete?: boolean
  licensedAgencyId?: string
  saveProgress?: SaveProgress
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    formFields: {
      // setting the spacing on the Grid adds to the width and subtracts from the margin, but not enough
      marginLeft: -2 * Spacing,
      marginRight: -2 * Spacing,
      flexDirection: 'column',
    },
    form: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    formFieldContainer: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'auto',
      padding: theme.spacing(8),
    },
    formOverlay: {
      background: 'white',
      opacity: '.38',
      position: 'absolute',
      left: 0,
      top: 0,
      right: 0,
      bottom: 0,
    },
    key: {
      color: theme.palette.neutralButton.main,
    },
    loggingOption: {
      paddingLeft: theme.spacing(8),
      paddingRight: theme.spacing(8),
    },
    container: {
      paddingLeft: theme.spacing(8),
      paddingRight: theme.spacing(8),
    },
    accordion: {
      '& .MuiAccordionDetails-root': {
        flexDirection: 'column',
      },
    },
    certStatus: {
      marginTop: theme.spacing(8),
    },
    fileBrowse: {
      alignSelf: 'center',
    },
    fileName: {
      fontWeight: 'bold',
    },
    fileNameFormikField: {
      display: 'none',
    },
    fileClear: {
      marginTop: theme.spacing(-1.5),
    }
  }),
)

export const EditCustomMapLayer: React.FunctionComponent<EditCustomMapLayerProps> = ({
  customMapLayer,
  onClose,
  onSave,
  onAdd,
  onDelete,
  canDelete,
  saveText,
  confirmColor = 'primary',
  licensedAgencyId,
  saveProgress,
}) => {
  const classes = useStyles({})

  const {
    user: authenticatedUser,
    authorizedAgencies: authorizedAgencies,
    agencies: agencies,
  } = useAuth()
  const agencyHook = useAgency()
  const { user: currentUser } = useAuth()
  
  React.useEffect(() => {
    const loadAgencyData = async () => {
      await agencyHook.getAuthorizedAgencies()
      await agencyHook.getAgencies()
    }

    if (
      authenticatedUser !== null &&
      (authenticatedUser.isSupport ||
        (authenticatedUser.authorizedLicensedAgencies !== null &&
          authenticatedUser.authorizedLicensedAgencies.length > 1))
    ) {
      loadAgencyData()
    }
  }, [authenticatedUser])

  if (!licensedAgencyId) return null

  const { customMapLayers } = useCurrentAgencyCustomMapLayers(licensedAgencyId)

  const [file, setFile] = React.useState<File | null>(null)
  const [fileName, setFileName] = React.useState<string>('')
  const [isConfirmingDelete, setIsConfirmingDelete] = React.useState<boolean>(false)
  const [showFileTypeErrorMessage, setShowFileTypeErrorMessage] = React.useState<boolean>(false)
  const [selectedAgenciesForLayer, setSelectedAgenciesForLayer] = React.useState<string[]>([])

  React.useEffect(() => {
    setFileName(file?.name ?? customMapLayer.fileName)
    setSelectedAgenciesForLayer(
        customMapLayer === null || customMapLayer.sharedAgencies === null
            ? []
            : customMapLayer.sharedAgencies,
    )
  }, [customMapLayer])

  const onEdit = async (values: MapLayer, actions: any) => {

    values.fileName = fileName
    values.sharedAgencies = selectedAgenciesForLayer

    if (customMapLayer.id === 'NEW') {
      await onAdd?.(values, file)
    } else {
      // return right away since onSave closes the action sheet
      onSave(values, file)
      return
    }

    actions.setSubmitting(false)
  }

  const onDeleteClick = () => {
    setIsConfirmingDelete(false)

    onDelete && onDelete()
  }

  const isAdding = customMapLayer.id === 'NEW'
  const isEditing = customMapLayer.id !== 'NEW'

  const customMapLayerNameValidator = React.useMemo(() => {
    const customMapLayerNames = new Set<string>()
    // ignore the current layer name since we need to be able to save it with the current name
    customMapLayers
      .filter((item) => item.id !== customMapLayer.id)
      .map((item) => customMapLayerNames.add(item.name))

    return (name: string) => !customMapLayerNames.has(name)
  }, [customMapLayers, customMapLayer.id])

  const editCustomMapLayerFormSchema = React.useMemo(() => {
    return Yup.object().shape({
      name: Yup.string()
        .test(
          'Name is unique',
          'The custom map layer name must be unique.',
          customMapLayerNameValidator,
        )
        .max(50, 'The Name cannot exceed 50 characters.')
        .required('The map layer name field is required.'),
      displayName: Yup.string()
        .max(10, 'The app name cannot exceed 10 characters.')
        .required('The app name field is required.'),
    })
  }, [customMapLayerNameValidator])

  const customMapLayerWithDefaults: MapLayer = {
    ...customMapLayer,
  }

  const toggleAgencyForLayer = (agencyId: string) => {
    if (selectedAgenciesForLayer.includes(agencyId)) {
      setSelectedAgenciesForLayer([
        ...selectedAgenciesForLayer.filter((x) => x !== agencyId),
      ])
    } else {
      setSelectedAgenciesForLayer([...selectedAgenciesForLayer, agencyId])
    }
  }

  const isFileTypeValid = (fileName: string) => {
    var fileNameLower = fileName.toLowerCase();
    if(fileNameLower && (fileNameLower.endsWith('.geojson') || fileNameLower.endsWith('.kml')))
    {
      setShowFileTypeErrorMessage(false);
      return true;
    }
    else
    {
      setShowFileTypeErrorMessage(true);
      return false;
    }
  }

  const isFormReadOnly = !!saveProgress
  const inputFile = React.useRef<HTMLInputElement | null>(null);

  const onFileBrowseClick = () => {
    // `current` points to the mounted file input element
    if(inputFile.current)
      inputFile.current.click();
  };

  return (
    <React.Fragment>
      <Formik
        enableReinitialize
        initialValues={customMapLayerWithDefaults}
        validationSchema={editCustomMapLayerFormSchema}
        onSubmit={onEdit}
        validateOnBlur={saveText === 'Close' ? false : true}
      >
        {(formikProps) => (
          <Form
            className={classes.form}
            onSubmit={(e: React.FormEvent<HTMLFormElement> | undefined) => {
              if(isFileTypeValid(fileName))
              {
                formikProps.handleSubmit(e)
              }
              e?.preventDefault()
            }} 
            >

            <Box className={classes.formFieldContainer}>              
              <Grid container spacing={8}>
                  <Grid container item xs={12}>
                    <FormikTextField
                        formikProps={formikProps}
                        fieldName="name"
                        label="Name"
                        fullWidth
                        readOnly={isFormReadOnly}
                        required />
                    <Typography
                      variant="caption"
                      display="inline"
                      color="textSecondary"
                    >
                      50 character limit.
                    </Typography>
                  </Grid>
                  <Grid container item xs={12}>
                    <FormikTextField
                        formikProps={formikProps}
                        fieldName="displayName"
                        label="Abbreviated Name"
                        fullWidth
                        readOnly={isFormReadOnly}
                        required />
                    <Typography
                      variant="caption"
                      display="inline"
                      color="textSecondary"
                    >
                      Layer name displayed in Field Ops mobile app. 10 character limit.
                    </Typography>
                  </Grid>
                  <Grid container item xs={12} spacing={4}>
                    <Grid item>
                      <Typography
                        variant="caption"
                        display="inline"
                        color="textSecondary"
                      >
                        Choose map layer file to upload (GeoJSON or KML file types, 100mb limit)
                      </Typography>
                    </Grid>
                    <Grid container item alignItems="center" justifyContent="center">
                      <input 
                        id='fileBrowseInput' 
                        type='file' 
                        accept=".geojson,.kml" 
                        disabled={isFormReadOnly} 
                        ref={inputFile} 
                        style={{display: 'none'}}
                        onChange={e => {
                          if(e.target.files && e.target.files[0] != null) {
                            const file = e.target.files[0]
                            setFile(file)
                            setFileName(file.name)
                            isFileTypeValid(file.name)
                            e.target.value = "";
                          }}
                        }
                      />
                      <Button
                        className={classes.fileBrowse}
                        variant="outlined"
                        onClick={onFileBrowseClick}
                      >
                        Browse
                      </Button>
                    </Grid>
                    {fileName &&
                      <Grid container item alignContent="center">
                        <Grid item>
                          <Typography
                            className={classes.fileName}
                            variant="body2"
                            display="inline"
                            color="textSecondary"
                          >
                            {fileName}
                          </Typography>
                          <IconButton
                            className={classes.fileClear}
                            edge="end"
                            aria-label="delete"
                            disabled={isFormReadOnly}
                            onClick={() => {
                              setFile(null)
                              setFileName('')
                              setShowFileTypeErrorMessage(false);
                            }}
                            size="large">
                            <Clear />
                          </IconButton>
                        </Grid>
                      </Grid>                      
                    }
                    {showFileTypeErrorMessage &&
                      <Grid item>
                        <Typography
                          className={classes.fileName}
                          variant="body2"
                          display="inline"
                          color="error"
                        >
                          Please select a GeoJSON or KML map layer file
                        </Typography>
                      </Grid>
                    }
                  </Grid>
                  {authenticatedUser !== null &&
                    (authenticatedUser.isSupport ||
                      (authenticatedUser.authorizedLicensedAgencies !== null &&
                        authenticatedUser.authorizedLicensedAgencies.length > 1)) && (
                    <Grid item xs={12} key="AgenciesForLayer">
                      <Typography
                        variant="caption"
                        display="inline"
                        color="textSecondary"
                      >
                        Agencies Using Layer
                      </Typography>
                      
                      {agencies.map((agency) => {
                        return (
                          <Grid
                            key={`AgencyForLayer::${agency.licensedAgencyId}`}
                            xs={12}
                            item
                          >
                            <Checkbox
                                label={agency.name}
                                id={agency.licensedAgencyId}
                                disabled={
                                    isFormReadOnly ||
                                    customMapLayer.ownerAgency === agency.licensedAgencyId ||
                                    (!authenticatedUser.isSupport &&
                                        authorizedAgencies.filter(
                                            (x) =>
                                                x.licensedAgencyId ==
                                                agency.licensedAgencyId,
                                        ).length == 0)
                                }
                                checked={
                                    customMapLayer.ownerAgency === agency.licensedAgencyId ||
                                    (selectedAgenciesForLayer !== null &&
                                        selectedAgenciesForLayer.includes(
                                            agency.licensedAgencyId,
                                        ))
                                }
                                onChange={() =>
                                    toggleAgencyForLayer(agency.licensedAgencyId)
                                }
                            />
                          </Grid>
                        )
                      })}
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <FormikTextField
                      formikProps={formikProps}
                      fieldName="notes"
                      label="Notes"
                      disabled={isFormReadOnly}
                      fullWidth
                      multiline
                      rows={5}
                    />
                  </Grid>
              </Grid>
            </Box>

            {!!saveProgress &&
              <Grid className={classes.formOverlay} />
            }

            <Box className={classes.spacer} />

            {!!saveProgress && saveProgress === 'indeterminate' &&
                <LinearProgress variant='indeterminate' />
            }

            {!!saveProgress && saveProgress  !== 'indeterminate' &&
                <LinearProgress variant='determinate' value={saveProgress} />
            }

            <EditModalActionBar
              canDelete={canDelete && !saveProgress}
              onDelete={() => setIsConfirmingDelete(true)}
              onCancel={onClose}
              onSave={saveText === 'Close' ? onClose : () => {}}
              formikProps={formikProps}
              saveText={!!saveProgress ? "Saving..." : saveText}
              canSubmit={!saveProgress}
              canCancel={!saveProgress}
              saveColor={confirmColor}
              saveButtonType={saveText === 'Close' ? 'button' : 'submit'}
              deleteText="Delete Custom Map Layer"
            />
          </Form>
        )}
      </Formik>
      {isEditing && (
        <ConfirmDeleteModal
          onCancel={() => setIsConfirmingDelete(false)}
          onConfirm={onDeleteClick}
          open={isConfirmingDelete}
          line1Text="Please confirm you wish to delete this custom map layer.  This action cannot be reversed."
          line2Text={`Custom map layer: ${customMapLayer.name}`}
          inputLabelText="Please enter DELETE to confirm deletion of this custom map layer."
        />
      )}
    </React.Fragment>
  )
}
